import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CardGiftcardRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { corporatePersonnel_addCreditToAll, corporatePersonnel_query } from "../../network/api/corporatePersonnel.api";
import { corporate_getCorporateGroups } from "../../network/api/corporate.api";
import { sport_getAll } from "../../network/api/sport.api";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { getCurrentPersianMonthName, getUserFixedName, toPriceWithComma } from "../../helper/utils";
import { CreditUsageCategoryEnum } from "../../helper/enums/CreditUsageCategoryEnum";
import { getUnifiedSportsList } from "../../helper/constants/sportsConstants";

import _AllocationConfirmDialog from "./partials/_AllocationConfirmDialog";
import _AllocationRecipientStrategy from "./partials/_AllocationRecipientStrategy";
import _AllocationFormFields from "./partials/_AllocationFormFields";
import _CreditBudgetPreview from "./partials/_CreditBudgetPreview";

const IncreaseGroupCredit = () => {
    const theme = useTheme();
    const error = useContext(ErrorContext);
    const navigate = useNavigate();
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const minCredit = 1000;
    const [groups, setGroups] = useState([]);
    const [allocationMode, setAllocationMode] = useState("ALL"); // ALL, GROUP, CUSTOM
    const [selectedGroupId, setSelectedGroupId] = useState("");
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [personnelList, setPersonnelList] = useState([]);
    const [loadingPersonnel, setLoadingPersonnel] = useState(false);
    const [searchPersonnel, setSearchPersonnel] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [openModalConfirm, setOpenModalConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Categories and Blocked Sports states
    const [usageCategories, setUsageCategories] = useState(["SPORT"]);
    const [blockedSportIds, setBlockedSportIds] = useState([]);
    const [sportsList, setSportsList] = useState([]);

    const defaultPackageName = `اعتبار ${getCurrentPersianMonthName()}`;

    const [formData, setFormData] = useState({
        Name: defaultPackageName,
        CreditAmount: "1000000",
        ExpireDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    });

    useEffect(() => {
        document.title = 'تخصیص اعتبار به پرسنل | جیم‌پین سازمانی';
        getPersonnelGroup();
        loadSports();
    }, [corporate?.Id]);

    function getPersonnelGroup() {
        if (!corporate?.Id) return;
        corporate_getCorporateGroups({ Id: corporate.Id })
            .then(result => {
                const list = result?.data?.Data || [];
                setGroups(list);
                if (list.length > 0) {
                    setSelectedGroupId(list[0].Id);
                    setSelectedGroupIds([list[0].Id]);
                }
            })
            .catch(() => setGroups([]));
    }

    function loadSports() {
        sport_getAll()
            .then(res => {
                setSportsList(getUnifiedSportsList(res?.data?.Data || []));
            })
            .catch(() => setSportsList(getUnifiedSportsList([])));
    }

    function loadPersonnelForCustom() {
        if (!corporate?.Id || personnelList.length > 0) return;
        setLoadingPersonnel(true);
        corporatePersonnel_query({
            CorporateId: corporate.Id,
            queryType: "FILTER",
            paging: { Page: 0, Size: 200, Desc: true },
        })
            .then(result => {
                setPersonnelList(result?.data?.Data?.content || []);
            })
            .catch(() => { })
            .finally(() => setLoadingPersonnel(false));
    }

    const handleModeChange = (newMode) => {
        setAllocationMode(newMode);
        if (newMode === "CUSTOM") {
            loadPersonnelForCustom();
        }
    };

    const handleCategoryToggle = (catCode) => {
        const category = CreditUsageCategoryEnum[catCode];
        if (category && !category.active) return;
        if (usageCategories.includes(catCode)) {
            const next = usageCategories.filter(c => c !== catCode);
            setUsageCategories(next.length > 0 ? next : ["SPORT"]);
        } else {
            setUsageCategories([...usageCategories, catCode]);
        }
    };

    const selectedGroups = groups.filter(g =>
        selectedGroupIds.some(id => String(id) === String(g.Id))
    );
    const selectedGroup = selectedGroups.length > 0 ? selectedGroups[0] : null;

    let recipientLabel = "تمامی پرسنل سازمان";
    let recipientCount = null;

    if (allocationMode === "ALL") {
        recipientLabel = "تمامی کارمندان سازمان";
        recipientCount = null;
    } else if (allocationMode === "GROUP") {
        if (selectedGroups.length === 0) {
            recipientLabel = "هیچ گروهی انتخاب نشده";
            recipientCount = 0;
        } else if (selectedGroups.length === 1) {
            recipientLabel = `کارمندان گروه «${selectedGroups[0].Name}»`;
            recipientCount = selectedGroups[0]?.UserCount || 0;
        } else {
            const names = selectedGroups.map(g => g.Name).join('، ');
            recipientLabel = `پرسنل عضو گروه‌های (${names})`;
            recipientCount = selectedGroups.reduce((acc, g) => acc + (g.UserCount || 0), 0);
        }
    } else if (allocationMode === "CUSTOM") {
        recipientLabel = `${selectedUsers.length} کارمند انتخاب‌شده`;
        recipientCount = selectedUsers.length;
    }

    const rawCreditAmount = parseInt(formData.CreditAmount || "0", 10) || 0;
    const estimatedTotal = recipientCount !== null ? (rawCreditAmount * recipientCount) : null;

    const handleUserToggle = (user) => {
        if (selectedUsers.some(u => u.Id === user.Id)) {
            setSelectedUsers(selectedUsers.filter(u => u.Id !== user.Id));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
    };

    const handleSelectAllUsers = (filtered) => {
        const allInFilter = filtered.every(u => selectedUsers.some(su => su.Id === u.Id));
        if (allInFilter) {
            const filteredIds = new Set(filtered.map(u => u.Id));
            setSelectedUsers(selectedUsers.filter(u => !filteredIds.has(u.Id)));
        } else {
            const existingIds = new Set(selectedUsers.map(u => u.Id));
            const toAdd = filtered.filter(u => !existingIds.has(u.Id));
            setSelectedUsers([...selectedUsers, ...toAdd]);
        }
    };

    const filteredPersonnel = personnelList.filter(p => {
        if (!searchPersonnel.trim()) return true;
        const name = getUserFixedName(p?.User) || "";
        const phone = p?.User?.PhoneNumber || "";
        const query = searchPersonnel.trim().toLowerCase();
        return name.toLowerCase().includes(query) || phone.includes(query);
    });

    const blockedSportsObjects = sportsList.filter(s =>
        blockedSportIds.some(bId => String(bId) === String(s.Id))
    );

    function handleOpenConfirm(e) {
        e.preventDefault();

        if (!rawCreditAmount || rawCreditAmount < minCredit) {
            error.showError({ message: `حداقل مبلغ اعتبار ${toPriceWithComma(minCredit)} تومان می‌باشد` });
            return;
        }
        if (!formData.Name?.trim()) {
            error.showError({ message: "لطفاً عنوانی برای بسته اعتباری وارد کنید" });
            return;
        }
        if (allocationMode === "GROUP" && selectedGroupIds.length === 0) {
            error.showError({ message: "لطفاً حداقل یک گروه سازمانی را انتخاب نمایید" });
            return;
        }
        if (allocationMode === "CUSTOM" && selectedUsers.length === 0) {
            error.showError({ message: "لطفاً حداقل یک نفر از پرسنل را انتخاب نمایید" });
            return;
        }
        if (corporate?.ContractType !== "ALPHA" && !formData.ExpireDate) {
            error.showError({ message: "تعیین تاریخ انقضا برای اعتبارات الزامی است" });
            return;
        }

        setOpenModalConfirm(true);
    }

    function addPersonnelCredit() {
        setSubmitting(true);
        const payload = {
            CorporateId: corporate?.Id,
            Name: formData.Name.trim(),
            CreditAmount: rawCreditAmount,
            ExpireDate: formData.ExpireDate ? new Date(formData.ExpireDate).getTime() : undefined,
            UsageCategories: usageCategories,
            BlockedSportIds: usageCategories.includes("SPORT") ? blockedSportIds : [],
        };

        if (allocationMode === "GROUP") {
            if (selectedGroupIds.length === 1) {
                payload.GroupId = selectedGroupIds[0];
                payload.GroupIds = selectedGroupIds;
            } else if (selectedGroupIds.length > 1) {
                payload.GroupIds = selectedGroupIds;
            }
        } else if (allocationMode === "CUSTOM") {
            payload.PersonnelIds = selectedUsers.map(u => u.Id);
        }

        corporatePersonnel_addCreditToAll(payload)
            .then(() => {
                setSubmitting(false);
                setOpenModalConfirm(false);
                error.showError({ message: "اعتبارات با موفقیت به پرسنل تخصیص یافت" });
                navigate("/personnel");
            })
            .catch(ca => {
                setSubmitting(false);
                const msg = ca?.response?.data?.Message || "خطا در تخصیص اعتبارات";
                error.showError({ message: msg });
            });
    }

    return (
        <Container maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
            {/* Confirmation Dialog */}
            <_AllocationConfirmDialog
                open={openModalConfirm}
                onClose={() => setOpenModalConfirm(false)}
                onSubmit={addPersonnelCredit}
                submitting={submitting}
                formData={formData}
                recipientLabel={recipientLabel}
                rawCreditAmount={rawCreditAmount}
                recipientCount={recipientCount}
                estimatedTotal={estimatedTotal}
                usageCategories={usageCategories}
                blockedSportsObjects={blockedSportsObjects}
            />

            {/* Executive Page Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3.5,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 20px rgba(45, 107, 226, 0.3)',
                        }}
                    >
                        <CardGiftcardRounded sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            تخصیص اعتبار و شارژ پرسنل
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            تخصیص اعتبار ورزشی به پرسنل، گروه‌ها یا افراد منتخب
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Recipient Strategy Mode Card */}
            <_AllocationRecipientStrategy
                allocationMode={allocationMode}
                handleModeChange={handleModeChange}
                groups={groups}
                selectedGroupId={selectedGroupId}
                setSelectedGroupId={setSelectedGroupId}
                selectedGroupIds={selectedGroupIds}
                setSelectedGroupIds={setSelectedGroupIds}
                searchPersonnel={searchPersonnel}
                setSearchPersonnel={setSearchPersonnel}
                selectedUsers={selectedUsers}
                filteredPersonnel={filteredPersonnel}
                handleSelectAllUsers={handleSelectAllUsers}
                handleUserToggle={handleUserToggle}
                loadingPersonnel={loadingPersonnel}
            />

            {/* Form Section */}
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <_AllocationFormFields
                        formData={formData}
                        setFormData={setFormData}
                        rawCreditAmount={rawCreditAmount}
                        usageCategories={usageCategories}
                        handleCategoryToggle={handleCategoryToggle}
                        blockedSportIds={blockedSportIds}
                        setBlockedSportIds={setBlockedSportIds}
                        sportsList={sportsList}
                        handleOpenConfirm={handleOpenConfirm}
                    />
                </Grid>

                {/* Live Budget Breakdown Column */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <_CreditBudgetPreview
                        recipientLabel={recipientLabel}
                        rawCreditAmount={rawCreditAmount}
                        recipientCount={recipientCount}
                        usageCategories={usageCategories}
                        blockedSportIds={blockedSportIds}
                        estimatedTotal={estimatedTotal}
                    />
                </Grid>
            </Grid>
        </Container>
    );
};

export default IncreaseGroupCredit;
